import React from "react";

import _ from "lodash";

import useForm from "commons/hooks/useForm";

import { FormHookType } from "commons/types/form";
import { ObjectPropertyType } from "commons/types/validation";

import { useBlockGroupContext } from "components/form/Blocks";
import { AttachmentType } from "components/ui/AttachmentPicker";

import { useSaveActionsContext } from "context/save";

import useSectionTwoImagesVerticalApi from "hooks/api/private/blocks/useSectionTwoImagesVerticalApi";
import useMobileOrDesktopFile from "hooks/useMobileOrDesktopFile";
import { ButtonDeleteInterface } from "hooks/useTitleFunctionsTopBlock";

import { SectionTwoImagesBody, SectionTwoImagesDetails } from "types/api/blocks/sectionTwoImages";
import { DesktopMobileForm } from "types/api/commons";
import { DesktopOrMobile } from "types/enums";

import { converterAttachmentToFileWithPayload, createAttachmentType } from "utils/utils";
import { getExtraErrorsDescription, notEmptyMedia } from "utils/validators";

export type SectionTwoImagesForm = {
    id: string | undefined;
    left_img_id: string | undefined;
    right_img_id: string | undefined;
    left_img: DesktopMobileForm;
    right_img: DesktopMobileForm;
};

type SectionTwoImagesType = {
    setRightImage: (fileToSet: AttachmentType, target: DesktopOrMobile) => void;
    setLeftImage: (fileToSet: AttachmentType, target: DesktopOrMobile) => void;
    buttonDelete: ButtonDeleteInterface;
    formHook: FormHookType<SectionTwoImagesForm>;
};

function useSectionTwoImages(): SectionTwoImagesType {
    const {
        saveSectionTwoImagesVertical,
        getOneSectionTwoImagesVertical,
        deleteSectionTwoImagesVertical,
    } = useSectionTwoImagesVerticalApi();

    const { blockGroupCompositionId, blockGroupId, blockId, graphicalOrder } =
        useBlockGroupContext();

    const schema: ObjectPropertyType = React.useMemo(() => {
        return {
            type: "object",
            properties: {
                id: {
                    type: "simple",
                    validators: [],
                },
                left_img_id: {
                    type: "simple",
                    validators: [],
                },
                right_img_id: {
                    type: "simple",
                    validators: [],
                },
                left_img: notEmptyMedia,
                right_img: notEmptyMedia,
            },
        };
    }, []);

    const initialValues: SectionTwoImagesForm = React.useMemo(() => {
        return {
            id: undefined,
            left_img_id: undefined,
            left_img: {
                id: undefined,
                desktop: createAttachmentType({ id: null, label: "Desktop" }),
                mobile: createAttachmentType({ id: null, label: "Mobile" }),
            },
            right_img_id: undefined,
            right_img: {
                id: undefined,
                desktop: createAttachmentType({ id: null, label: "Desktop" }),
                mobile: createAttachmentType({ id: null, label: "Mobile" }),
            },
        };
    }, []);

    const formHook = useForm<SectionTwoImagesForm>(
        schema,
        initialValues,
        getExtraErrorsDescription
    );

    const { showAllError, setForm, isValid, form } = formHook;

    const { setDesktopMobileImage: setLeftImage } = useMobileOrDesktopFile({
        setForm: setForm,
        path: "left_img",
    });

    const { setDesktopMobileImage: setRightImage } = useMobileOrDesktopFile({
        setForm: setForm,
        path: "right_img",
    });

    const [sectionTwoImagesDetails, setSectionTwoDetails] = React.useState<
        SectionTwoImagesDetails | undefined
    >(undefined);

    React.useEffect(() => {
        const [apiCall, abortController] = getOneSectionTwoImagesVertical(
            { id: blockGroupCompositionId },
            { rejectNotFound: true }
        );

        apiCall.then(setSectionTwoDetails).catch((error) => console.log(error));

        return () => abortController.abort();
    }, [setSectionTwoDetails, getOneSectionTwoImagesVertical, blockGroupCompositionId]);

    React.useEffect(() => {
        if (sectionTwoImagesDetails) {
            setForm((prevForm) => {
                const newForm = _.cloneDeep(prevForm);

                newForm.id = sectionTwoImagesDetails.id;
                newForm.left_img_id = sectionTwoImagesDetails.left_img_id;
                newForm.right_img_id = sectionTwoImagesDetails.right_img_id;

                if (sectionTwoImagesDetails.left_img) {
                    newForm.left_img = {
                        id: sectionTwoImagesDetails.left_img.id,
                        desktop: createAttachmentType({
                            id: sectionTwoImagesDetails.left_img.desktop.id,
                            label: "Desktop",
                            filename: sectionTwoImagesDetails.left_img.desktop.filename,
                            status: 2,
                        }),
                        mobile: createAttachmentType({
                            id: sectionTwoImagesDetails.left_img.mobile.id,
                            label: "Mobile",
                            filename: sectionTwoImagesDetails.left_img.mobile.filename,
                            status: 2,
                        }),
                    };
                }

                if (sectionTwoImagesDetails.right_img) {
                    newForm.right_img = {
                        id: sectionTwoImagesDetails.right_img.id,
                        desktop: createAttachmentType({
                            id: sectionTwoImagesDetails.right_img.desktop.id,
                            label: "Desktop",
                            filename: sectionTwoImagesDetails.right_img.desktop.filename,
                            status: 2,
                        }),
                        mobile: createAttachmentType({
                            id: sectionTwoImagesDetails.right_img.mobile.id,
                            label: "Mobile",
                            filename: sectionTwoImagesDetails.right_img.mobile.filename,
                            status: 2,
                        }),
                    };
                }

                return newForm;
            });
        }
    }, [setForm, sectionTwoImagesDetails]);

    const { addSaveAction } = useSaveActionsContext();

    React.useEffect(() => {
        addSaveAction(blockGroupCompositionId, () => {
            if (isValid) {
                const sectionTwoImagesBody: SectionTwoImagesBody = {
                    id: blockGroupCompositionId,
                    left_img: {
                        id: form.left_img.id,
                        desktop: converterAttachmentToFileWithPayload(form.left_img.desktop),
                        mobile: converterAttachmentToFileWithPayload(form.left_img.mobile),
                    },
                    right_img: {
                        id: form.right_img.id,
                        desktop: converterAttachmentToFileWithPayload(form.right_img.desktop),
                        mobile: converterAttachmentToFileWithPayload(form.right_img.mobile),
                    },
                };

                const [apiCall] = saveSectionTwoImagesVertical(sectionTwoImagesBody);

                const promises: Promise<unknown>[] = [];

                promises.push(apiCall);

                return Promise.all(promises);
            } else {
                showAllError();
                return Promise.reject("INVALID_FORM");
            }
        });
    }, [
        saveSectionTwoImagesVertical,
        addSaveAction,
        showAllError,
        blockGroupCompositionId,
        isValid,
        form,
        blockGroupId,
        blockId,
        graphicalOrder,
    ]);

    return React.useMemo(
        (): SectionTwoImagesType => ({
            buttonDelete: deleteSectionTwoImagesVertical,
            setLeftImage,
            setRightImage,
            formHook,
        }),
        [deleteSectionTwoImagesVertical, setRightImage, setLeftImage, formHook]
    );
}

export default useSectionTwoImages;
